import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, verifyLoginOtp } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetData, setResetData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    setLoading(false);

    if (result.requiresOtp) {
      setOtpChallenge(result.challengeId);
      toast.success("A verification code was sent to your email.");
    } else if (result.success) {
      toast.success("Welcome back!");
      navigate("/dashboard");
    } else {
      toast.error(result.error || "Invalid credentials");
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    const result = await verifyLoginOtp(otpChallenge, otp); setLoading(false);
    if (result.success) navigate("/dashboard"); else toast.error(result.error);
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail }); setResetData(res.data.challenge_id); toast.success("If the account exists, a code was sent."); }
    catch { toast.error("Unable to start password reset"); } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, { challenge_id: resetData, code: otp, new_password: loginData.password }); toast.success("Password reset. You can now sign in."); setForgotOpen(false); setResetData(null); setOtp(""); }
    catch (error) { toast.error(error.response?.data?.detail || "Unable to reset password"); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1200&q=70"
          alt=""
          aria-hidden="true"
          width="1200"
          height="800"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-primary/80 flex flex-col items-center justify-center gap-4">
          <h1
            className="text-6xl font-bold text-white"
            style={{ letterSpacing: "-0.03em" }}
          >
            AgencyOS
          </h1>
          <p className="text-white/80 text-lg">
            Your Complete Agency Operations Platform
          </p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div className="text-center lg:hidden">
            <h1
              className="text-4xl font-bold mb-1"
              style={{ letterSpacing: "-0.02em" }}
            >
              AgencyOS
            </h1>
            <p className="text-muted-foreground text-sm">
              Agency operations platform
            </p>
          </div>

          <div className="space-y-2">
            <h2
              className="text-2xl font-semibold"
              style={{ letterSpacing: "-0.01em" }}
            >
              Sign in to your account
            </h2>
            <p className="text-sm text-muted-foreground">
              Contact your owner to get access credentials.
            </p>
          </div>

          {otpChallenge ? <form onSubmit={handleOtp} className="space-y-5"><p className="text-sm text-muted-foreground">Enter the 6-digit code sent to your email.</p><Input inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder="123456" /><Button type="submit" className="w-full" disabled={loading}>{loading ? "Verifying…" : "Verify code"}</Button></form> : <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                placeholder="you@agency.com"
                value={loginData.email}
                onChange={(e) =>
                  setLoginData({ ...loginData, email: e.target.value })
                }
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) =>
                  setLoginData({ ...loginData, password: e.target.value })
                }
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>}

          <button type="button" className="w-full text-sm text-primary underline" onClick={() => setForgotOpen(!forgotOpen)}>Forgot password?</button>
          {forgotOpen && <div className="space-y-3 rounded-md border p-4"><p className="text-sm font-medium">Reset password</p>{!resetData ? <form onSubmit={handleForgot} className="space-y-3"><Input type="email" placeholder="you@agency.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required /><Button type="submit" variant="outline" className="w-full">Send email code</Button></form> : <form onSubmit={handleReset} className="space-y-3"><Input inputMode="numeric" maxLength={6} placeholder="Email code" value={otp} onChange={(e) => setOtp(e.target.value)} required /><Input type="password" minLength={12} placeholder="New password (12+ characters)" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required /><Button type="submit" variant="outline" className="w-full">Reset password</Button></form>}</div>}

          <p className="text-center text-xs text-muted-foreground">
            Don't have an account? Ask your agency owner to create one for you.
          </p>
        </div>
      </div>
    </div>
  );
}
